// https://mui.com/material-ui/react-autocomplete/#virtualization
// https://github.com/bvaughn/react-window

import {
  createContext,
  forwardRef,
  HTMLAttributes,
  ReactElement,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { ListChildComponentProps, VariableSizeList } from 'react-window';
import {
  Autocomplete,
  createFilterOptions,
  ListSubheader,
  Popper,
  PopperProps,
  SelectProps,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
import ListItemText from '@mui/material/ListItemText';
import { makeStyles } from '@mui/styles';
import { sort } from 'fast-sort';
import { EstateInputDataFormat, EstateInputDataFormatType } from 'queries';

type Props = Omit<SelectProps, 'variant'> & {
  label?: string;
  errorMessage?: string;
  options: EstateInputDataFormat[];
  onSelectChange: (context: string[]) => void;
};

const useStyles = makeStyles(() => ({
  adornment: {
    paddingLeft: '10px',
    opacity: 0.5,
  },
  input: {
    width: '100%',
    maxWidth: '100%',
  },
  switch: {
    width: '100%',
    maxWidth: '100%',
    marginTop: '10px',
    paddingTop: '10px',
  },
  textField: {
    '& .MuiInputBase-root': {
      padding: '0px',
    },
    '& .MuiInputBase-input': {
      height: '30px',
    },
  },
}));

const EstateInputDataFormatsMultipleSelectCheckmarks = ({
  label,
  errorMessage,
  options,
  onSelectChange,
  ...other
}: Props) => {
  const classes = useStyles();

  const [searchValue, setSearchValue] = useState<string>('');
  const [selectedOptions, setSelectedOptions] = useState<EstateInputDataFormat[]>([]);

  const [autoCompleteFilterOption, setAutoCompleteFilterOption] = useState<boolean>(true);

  const filterOptions = createFilterOptions({
    matchFrom: autoCompleteFilterOption ? 'start' : 'any',
    stringify: (option: EstateInputDataFormat) => option.name,
  });

  const selectableOptions = sort(
    options.filter(
      (option) => option.type === EstateInputDataFormatType.Data && !!option.templateFieldKey,
    ),
  ).asc((option) => option.name);

  const handleSelectFormatChange = (
    selectedEstateInputDataFormats: EstateInputDataFormat[] | null,
  ) => {
    if (!selectedEstateInputDataFormats) {
      return;
    }

    const updatedSelectedEstateInputDataFormats = selectedEstateInputDataFormats.map(
      (estateInputDataFormat) => estateInputDataFormat.id,
    );
    setSelectedOptions(selectedEstateInputDataFormats);
    onSelectChange(updatedSelectedEstateInputDataFormats);
  };

  /**
   * Called when rendering each row in the list
   * @param props - provided implicitly by react-window's VariableSizeList
   * @returns
   */
  const renderRow = (props: ListChildComponentProps) => {
    const { data, index, style } = props;
    const dataSet = data[index];

    if (dataSet.hasOwnProperty('group')) {
      const inlineStyle = {
        ...style,
        top: (style.top as number) + 8,
      };
      return (
        <ListSubheader key={dataSet.key} component="div" style={inlineStyle}>
          {dataSet.group}
        </ListSubheader>
      );
    }

    const { key, ...optionProps } = dataSet[0];
    const { name, templateFieldKey } = dataSet[1]; // contains the custom option object

    const isSelected = selectedOptions.some((option) => option.name === name);
    const label = templateFieldKey ? `${name} - ${templateFieldKey}` : `${name}`;

    return (
      <Typography key={key} component="li" {...optionProps} noWrap style={style}>
        <Checkbox checked={isSelected} />
        <ListItemText primary={label} />
      </Typography>
    );
  };

  const OuterElementContext = createContext<React.HTMLAttributes<HTMLElement>>({});

  const OuterElementType = forwardRef<HTMLDivElement>((props, ref) => {
    const outerProps = useContext(OuterElementContext);
    return <div ref={ref} {...props} {...outerProps} />;
  });

  // clears the cache being used by the virtualized list
  function useResetCache(data: any) {
    const ref = useRef<VariableSizeList>(null);
    useEffect(() => {
      if (ref.current != null) {
        ref.current.resetAfterIndex(0, true);
      }
    }, [data]);
    return ref;
  }

  // adapter for react-window
  const ListboxComponent = forwardRef<HTMLDivElement, HTMLAttributes<HTMLElement>>(
    function ListboxComponent(props, ref) {
      const { children, ...other } = props;
      const itemData: ReactElement<unknown>[] = [];

      // flatten the html elements to get all the items
      // these html elements are generated by internal MUI logic when creating the default listbox
      // when overwriting the default listbox, we need to manually extract the items
      (children as ReactElement<unknown>[]).forEach(
        (
          item: ReactElement<unknown> & {
            children?: ReactElement<unknown>[];
          },
        ) => {
          itemData.push(item);
          itemData.push(...(item.children || []));
        },
      );

      // getChildSize and getHeight are used to dynamically calculate the height of the listbox
      // component based on the number of items preventing a 600px container displaying 1 item
      // the upper limit is 600px and the items for this component are quite large at 50px each.
      const itemCount = itemData.length;
      const itemSize = 50;

      const getChildSize = (_: React.ReactElement<unknown>) => {
        return itemSize;
      };

      const getHeight = () => {
        if (itemCount > 20) {
          return 600;
        }
        return itemData.map(getChildSize).reduce((a, b) => a + b, 0);
      };

      const gridRef = useResetCache(itemCount);

      // react-window's VariableSizeList is used to render the listbox
      return (
        <div ref={ref}>
          <OuterElementContext.Provider value={other}>
            <VariableSizeList
              ref={gridRef}
              itemData={itemData}
              height={getHeight() + 2 * 8}
              width="100%"
              outerElementType={OuterElementType}
              innerElementType="ul"
              itemSize={(index) => getChildSize(itemData[index])}
              overscanCount={5}
              itemCount={itemCount}
            >
              {renderRow}
            </VariableSizeList>
          </OuterElementContext.Provider>
        </div>
      );
    },
  );

  const CustomPopper = (props: PopperProps) => {
    return <Popper {...props} placement="bottom-start" />;
  };

  useEffect(() => {
    if (other.value as string[]) {
      const selectedOptions = selectableOptions.filter((option) =>
        (other.value as string[]).includes(option.id),
      );
      setSelectedOptions(selectedOptions);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // sort options by category so they appear in the correct grouping when rendered
  // this will not happen by default when using the groupBy prop
  const sortedOptions = sort(options).by([
    { asc: (option: EstateInputDataFormat) => option.category },
  ]);

  // !Implement groupBy in renderRow function if declared here
  return (
    <div>
      <div className={classes.switch}>
        <Typography variant="caption">Filter at start?</Typography>
        <Switch
          checked={autoCompleteFilterOption}
          onChange={() => {
            setAutoCompleteFilterOption(!autoCompleteFilterOption);
          }}
          color="primary"
        />
      </div>
      <div className={classes.input}>
        {label && <Typography variant="caption">{label}</Typography>}
        <Autocomplete
          multiple
          fullWidth
          id="estate-input-data-format-multiple-select-checkmarks"
          value={selectedOptions}
          disableCloseOnSelect
          disablePortal
          disableListWrap
          disabledItemsFocusable
          options={sortedOptions}
          groupBy={(option) => option.category}
          filterOptions={filterOptions}
          inputValue={searchValue}
          getOptionLabel={(option) => `${option.name}`}
          onChange={(_: any, newValue: EstateInputDataFormat[] | null) =>
            handleSelectFormatChange(newValue)
          }
          renderOption={(props, option, state) => [props, option, state.index] as ReactNode}
          renderGroup={(params) => params as any}
          renderInput={(params) => (
            <TextField
              error={!!errorMessage}
              {...params}
              className={classes.textField}
              onChange={(evt) => {
                setSearchValue(evt.currentTarget.value);
              }}
            />
          )}
          ListboxProps={{
            sx: {
              maxHeight: 600,
            },
          }}
          ListboxComponent={ListboxComponent}
          PopperComponent={(props) => <CustomPopper {...props} />}
        />
      </div>
    </div>
  );
};
export default EstateInputDataFormatsMultipleSelectCheckmarks;
